import React, { Fragment, useEffect, useState } from "react";
import { Seo, Breadcrumb, ScrollTopBtn, TitleWrap } from "../components";
import { HeaderTwo, FooterTwo } from "../containers";

/**
 * PrivacyPolicy page
 * - Fixes React attribute issues (class -> className)
 * - Handles SSR-safety for window/localStorage
 * - Listens for `storage` changes to `cheese_lang` and updates UI
 * - Renders full Thai Privacy Notice content provided by the user
 * - Simple bilingual scaffold (TH primary; EN placeholder)
 */

const getInitialLang = () => {
  if (typeof window === "undefined") return "th";
  return window.localStorage.getItem("cheese_lang") || "th";
};

const PrivacyPolicy = () => {
  const [lang, setLang] = useState(getInitialLang);

  // Update on first mount (in case SSR hydration differs)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const v = window.localStorage.getItem("cheese_lang");
      setLang(v || "th");
    }
  }, []);

  // Listen for cross-tab language changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = () => {
      const v = window.localStorage.getItem("cheese_lang");
      setLang(v || "th");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isTH = lang === "th";

  return (
    <Fragment>
      <Seo title={isTH ? "นโยบายคุ้มครองข้อมูลส่วนบุคคล" : "Privacy Policy"} />
      <HeaderTwo />
      <Breadcrumb
        pageName={isTH ? "นโยบายคุ้มครองข้อมูลส่วนบุคคล" : "Privacy Policy"}
        bgImage="images/miw/bar_008.jpg"
      />

      <div className="container mb-100px">
        <TitleWrap className="text-center">
          <div className="d-flex justify-content-center pt-60px">
            <h1 className="title-line">
              {isTH ? "นโยบายคุ้มครองข้อมูลส่วนบุคคล" : "Privacy Policy"}
            </h1>
          </div>
        </TitleWrap>

        {isTH ? <ThaiNotice /> : <EnglishNoticePlaceholder />}
      </div>

      <FooterTwo />
      <ScrollTopBtn />
    </Fragment>
  );
};

export default PrivacyPolicy;

/**
 * Full Thai Privacy Notice (verbatim structure from the user, rendered semantically)
 */
const ThaiNotice = () => {
  return (
    <div className="row gx-60 gy-2">
      <div className="col-12">
        <section className="mb-4">
          <h2 className="h4">ประกาศความเป็นส่วนตัว (Privacy Notice)</h2>
          <p>
            บริษัท ชีส ดิจิตอล เน็ตเวิร์ค จำกัด (“บริษัท”) เคารพสิทธิความเป็นส่วนตัวและให้ความสำคัญต่อการคุ้มครองข้อมูลส่วนบุคคลที่เกี่ยวข้องหรือทำธุรกรรมกับบริษัท จึงได้จัดทำประกาศความเป็นส่วนตัวฉบับนี้ขึ้น เพื่อแจ้งให้ท่านในฐานะเจ้าของข้อมูลส่วนบุคคลทราบถึงวัตถุประสงค์และรายละเอียดของการเก็บรวบรวม ใช้ และ/หรือเปิดเผยข้อมูลส่วนบุคคล ตลอดจนสิทธิตามกฎหมายของท่านที่เกี่ยวข้องกับข้อมูลส่วนบุคคล
          </p>
        </section>

        <section className="mb-4">
          <h3 className="h5">1. ขอบเขตการบังคับใช้</h3>
          <p>
            ประกาศความเป็นส่วนตัวฉบับนี้มีผลบังคับใช้กับการเก็บรวบรวม ใช้ เปิดเผย และจัดเก็บข้อมูลส่วนบุคคลที่ดำเนินการโดยบริษัท จากกลุ่มลูกค้า คู่ค้า คู่สัญญา ผู้ถือหุ้น บุคลากร พนักงาน ผู้สมัครงาน หรือบุคคลอื่นใดที่บริษัทมีปฏิสัมพันธ์ด้วยในเชิงธุรกิจ
          </p>
        </section>

        <section className="mb-4">
          <h3 className="h5">2. คำนิยาม</h3>
          <ol>
            <li>
              <strong>“ข้อมูลส่วนบุคคล”</strong> หมายถึง ข้อมูลเกี่ยวกับบุคคลซึ่งทำให้สามารถระบุตัวบุคคลนั้นได้ ไม่ว่าทางตรงหรือทางอ้อม แต่ไม่รวมถึงข้อมูลของผู้ถึงแก่กรรมโดยเฉพาะ
            </li>
            <li>
              <strong>“ข้อมูลส่วนบุคคลอ่อนไหว”</strong> หมายถึง ข้อมูลที่เป็นเรื่องส่วนบุคคลโดยแท้ของบุคคล แต่มีความละเอียดอ่อนและอาจสุ่มเสี่ยงในการเลือกปฏิบัติอย่างไม่เป็นธรรม เช่น เชื้อชาติ เผ่าพันธุ์ ความคิดเห็นทางการเมือง ความเชื่อในลัทธิ ศาสนาหรือปรัชญา พฤติกรรมทางเพศ ประวัติอาชญากรรม ข้อมูลสุขภาพ ความพิการ ข้อมูลสหภาพแรงงาน ข้อมูลพันธุกรรม ข้อมูลชีวภาพ หรือข้อมูลอื่นใด ซึ่งกระทบต่อเจ้าของข้อมูลส่วนบุคคลในทำนองเดียวกันตามที่คณะกรรมการคุ้มครองข้อมูลส่วนบุคคลประกาศกำหนด
            </li>
            <li>
              <strong>“การประมวลผล”</strong> หมายถึง การดำเนินการเกี่ยวกับการเก็บรวบรวม ใช้ เปิดเผย การลบ หรือการทำลายข้อมูลส่วนบุคคล
            </li>
            <li>
              <strong>“เจ้าของข้อมูลส่วนบุคคล”</strong> หมายถึง บุคคลธรรมดาซึ่งเป็นเจ้าของข้อมูลส่วนบุคคลที่ข้อมูลส่วนบุคคลสามารถระบุตัวตนของบุคคลนั้นได้ ไม่ว่าทางตรงหรือทางอ้อม
            </li>
            <li>
              <strong>“ผู้ควบคุมข้อมูลส่วนบุคคล”</strong> หมายถึง บุคคลหรือนิติบุคคลซึ่งมีอำนาจหน้าที่ตัดสินใจเกี่ยวกับการเก็บรวบรวม ใช้ หรือเปิดเผยข้อมูลส่วนบุคคล
            </li>
            <li>
              <strong>“ผู้ประมวลผลข้อมูลส่วนบุคคล”</strong> หมายถึง บุคคลหรือนิติบุคคลซึ่งดำเนินการเกี่ยวกับการเก็บรวบรวม ใช้ หรือเปิดเผยข้อมูลส่วนบุคคลตามคำสั่งหรือในนามของบริษัท ทั้งนี้ บุคคลหรือนิติบุคคล ซึ่งดำเนินการดังกล่าว ไม่เป็นผู้ควบคุมข้อมูลส่วนบุคคล
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">3. การเก็บรวบรวมข้อมูลส่วนบุคคล</h3>
          <h4 className="h6">3.1 บริษัทเก็บรวบรวมข้อมูลส่วนบุคคลของท่านจากแหล่งใดบ้าง</h4>
          <ol>
            <li>
              จากท่านโดยตรง เช่น ข้อมูลที่ท่านกรอกหรือให้ไว้แก่บริษัท เพื่อจัดทำสัญญาบริการ สัญญาจ้าง สอบถาม ให้คำแนะนำ ร้องเรียน หรือข้อมูลจากการใช้บริการของบริษัท การติดต่อค้นหาผ่านเว็บไซต์ และช่องทางอื่น ๆ ของบริษัท
            </li>
            <li>
              บุคคลภายนอก เช่น ผู้ให้บริการข้อมูล พันธมิตรทางธุรกิจ โซเชียลมีเดีย และผู้ให้บริการโฆษณาต่าง ๆ
            </li>
            <li>
              การใช้คุกกี้ (Cookies) เทคโนโลยีการติดตาม (tracking technology) เมื่อท่านใช้งานเว็บไซต์ของบริษัท
            </li>
          </ol>
          <h4 className="h6">3.2 ข้อมูลส่วนบุคคลที่บริษัทเก็บรวบรวม</h4>
          <ol>
            <li>
              ข้อมูลส่วนบุคคล เช่น ชื่อ-นามสกุล อายุ วันเดือนปีเกิด เพศ สถานภาพสมรส เลขประจำตัวประชาชน เลขประจำตัวผู้เสียภาษี เลขหนังสือเดินทาง หรือข้อมูลยืนยันตัวตนอื่นที่ราชการออกให้ ที่อยู่ไปรษณีย์ สถานที่ทำงาน ใบอนุญาตประกอบวิชาชีพ ใบปริญญาบัตร ใบแสดงผลการศึกษา เลขบัญชีธนาคาร หมายเลขโทรศัพท์ อีเมล ไอดีไลน์, IP address, Cookie ID, ข้อมูลเบราว์เซอร์และข้อมูลอื่น ๆ เกี่ยวกับอุปกรณ์ที่ท่านใช้เพื่อเข้าถึงเว็บไซต์ และช่องทางการสื่อสารอื่น ๆ ของบริษัท
            </li>
            <li>
              ข้อมูลเกี่ยวกับกิจกรรมการใช้งานอินเทอร์เน็ต หรือโครงข่ายอิเล็กทรอนิกส์อื่น ๆ เช่น ประวัติในการเยี่ยมชม ค้นหา และปฏิสัมพันธ์ของท่านกับเว็บไซต์ของบริษัทหรือโฆษณาต่าง ๆ ข้อมูล ที่เก็บรวบรวมจากคุกกี้ และเทคโนโลยีการติดตามอื่น ๆ
            </li>
            <li>
              ข้อมูลเสียง ภาพ หรือข้อมูลอิเล็กทรอนิกส์อื่นที่อาจบ่งชี้ตัวบุคคลได้ ซึ่งอาจรวมถึง ข้อมูลเกี่ยวกับตำแหน่งที่ตั้งทางภูมิศาสตร์
            </li>
            <li>
              ข้อมูลส่วนบุคคลอ่อนไหว บริษัทจะเก็บรวบรวม ใช้ และ/หรือเปิดเผยข้อมูล ส่วนบุคคลที่ละเอียดอ่อน เช่น ข้อมูลเกี่ยวกับเชื้อชาติ ศาสนา ข้อมูลสุขภาพ ข้อมูลชีวภาพ ความทุพพลภาพ หรือข้อมูลที่ละเอียดอ่อนอื่นใด ต่อเมื่อบริษัทได้รับความยินยอมโดยชัดแจ้งจากท่าน หรือในกรณีที่บริษัท มีความจำเป็นตามที่กฎหมายอนุญาต
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">4. วัตถุประสงค์ในการเก็บรวบรวมหรือใช้ข้อมูลส่วนบุคคล</h3>
          <ol>
            <li>
              เพื่อบริการให้ตรงตามต้องการของท่าน บริษัทจะใช้ข้อมูลส่วนบุคคลของท่าน เพื่อที่ท่านจะสามารถได้รับบริการที่ตรงตามวัตถุประสงค์ของท่านตามสัญญาหรือตามที่ท่านร้องขอ
            </li>
            <li>
              เพื่อปฏิบัติหน้าที่ตามกฎหมาย ในการปฏิบัติตามกฎหมายที่เกี่ยวข้อง เช่น กฎหมายภาษีอากร กฎหมาย ป้องกันและปราบปรามการฟอกเงิน กฎหมายคอมพิวเตอร์ และกฎหมายอื่นๆ บริษัทมีการเก็บรวบรวม ใช้ และ/หรือเปิดเผยข้อมูลส่วนบุคคล เช่น ข้อมูลของกรรมการ ผู้มีอำนาจกระทำการแทน ตัวแทนของลูกค้านิติบุคคล เป็นต้น นอกจากนี้ บริษัทมีหน้าที่ปฏิบัติตามคำสั่งของหน่วยงานหรือเจ้าหน้าที่ผู้มีอำนาจตามกฎหมาย ทั้งนี้ หากบริษัทจำเป็นต้องใช้ และ/หรือเปิดเผยข้อมูลส่วนบุคคลของท่านสำหรับการปฏิบัติหน้าที่ตามกฎหมาย หรือการเข้าทำสัญญากับท่าน บริษัทอาจจะไม่สามารถส่งมอบผลิตภัณฑ์และ/หรือบริการให้แก่ท่าน (หรือไม่สามารถจัดหาผลิตภัณฑ์และ/หรือบริการให้แก่ท่านต่อไป) หากบริษัทไม่สามารถเก็บรวบรวมข้อมูลส่วนบุคคลของท่าน เมื่อมีการร้องขอ
            </li>
            <li>
              เพื่อการดำเนินงานที่จำเป็นภายใต้ประโยชน์โดยชอบด้วยกฎหมาย ของบริษัท หรือของบุคคลหรือนิติบุคคลอื่น โดยไม่เกินขอบเขตที่ท่านสามารถคาดหมายได้อย่างสมเหตุสมผล เช่น
              <ol>
                <li>การบันทึกเสียง การบันทึกภาพ CCTV ก่อนเข้าอาคาร</li>
                <li>การรักษาความสัมพันธ์กับลูกค้า เช่น การจัดการข้อร้องเรียน การประเมินความพึงพอใจ การดูแลลูกค้าโดยพนักงานของบริษัท การแจ้งเตือนหรือนำเสนอผลิตภัณฑ์และ/หรือบริการต่างๆ ประเภทเดียวกัน กับที่ท่านมีอยู่กับบริษัท ซึ่งเป็นประโยชน์กับท่าน</li>
                <li>การบริหารความเสี่ยง การกำกับตรวจสอบ การบริหารจัดการภายในองค์กร รวมถึง การป้องกันการกระทำผิดกฎหมาย เช่น การทุจริต การคุกคามทางไซเบอร์ การฟอกเงินและกฎหมายอื่น ๆ</li>
                <li>การทำให้ข้อมูลส่วนบุคคลเป็นข้อมูลที่ไม่สามารถระบุตัวบุคคลได้ (Anonymous Data)</li>
                <li>วัตถุประสงค์ทางธุรกิจ บริษัทมีการเก็บรวบรวมข้อมูลส่วนบุคคล เพื่อประโยชน์ในการวิเคราะห์ข้อมูลทางสถิติหรือประชาสัมพันธ์บริษัท เช่น การบันทึกภาพ บันทึกเสียงในการจัดประชุม อบรม สันทนาการ หรือการออกบูธ</li>
              </ol>
            </li>
            <li>
              เพื่อให้ท่านได้รับประโยชน์จากการใช้บริการ ตามที่ท่านได้ให้ความยินยอมไว้ เช่น เพื่อให้ท่านได้รับบริการที่ดียิ่งขึ้นและสอดคล้องกับความต้องการของท่าน เพื่อให้ท่านได้รับข้อเสนอ สิทธิประโยชน์พิเศษ คำแนะนำ และข่าวสารต่าง ๆ รวมถึงสิทธิในการเข้าร่วมกิจกรรมพิเศษ เป็นต้น
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">5. การเปิดเผยข้อมูลส่วนบุคคล</h3>
          <ol>
            <li>
              บริษัทจะไม่เปิดเผยข้อมูลส่วนบุคคลของท่านไปให้บุคคลใด โดยปราศจากความยินยอมและจะเปิดเผยตามวัตถุประสงค์ที่ได้มีการแจ้งไว้
            </li>
            <li>
              บริษัทอาจมีความจำเป็นในการเปิดเผยข้อมูลส่วนบุคคลของท่านให้แก่พันธมิตรทางธุรกิจ บุคคลอื่นทั้งในและต่างประเทศ เพื่อเป็นประโยชน์ต่อการดำเนินงานของบริษัทและการให้บริการแก่เจ้าของข้อมูล โดยในการเปิดเผยข้อมูลส่วนบุคคลให้แก่บุคคลดังกล่าว บริษัทจะดำเนินการให้บุคคลเหล่านั้น เก็บรักษาข้อมูลส่วนบุคคลไว้เป็นความลับและไม่นำไปใช้เพื่อวัตถุประสงค์อื่น นอกเหนือจากขอบเขตที่บริษัทได้กำหนดไว้
            </li>
            <li>
              บริษัทอาจเปิดเผยข้อมูลส่วนบุคคลของท่านภายใต้หลักเกณฑ์ที่กฎหมายกำหนด เช่น การเปิดเผยข้อมูลส่วนบุคคลต่อหน่วยงานราชการ หน่วยงานภาครัฐ หน่วยงานกำกับดูแล รวมถึงในกรณีที่มีการร้องขอให้เปิดเผยข้อมูล โดยอาศัยอำนาจตามกฎหมาย
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">6. ระยะเวลาในการเก็บรวบรวมข้อมูลส่วนบุคคล</h3>
          <p>
            บริษัทจะเก็บรักษาข้อมูลส่วนบุคคลของท่านไว้เป็นระยะเวลาเท่าที่จำเป็น เพื่อการดำเนินการตามวัตถุประสงค์ที่ระบุไว้ในนโยบายฉบับนี้ให้เสร็จสิ้น ซึ่งอาจจำเป็นต้องเก็บรักษาไว้ต่อไปภายหลังจากนั้น หากมีกฎหมายกำหนดหรืออนุญาตไว้ เช่น จัดเก็บไว้ตามกฎหมายว่าด้วยการป้องกันและปราบปรามการฟอกเงิน จัดเก็บไว้เพื่อวัตถุประสงค์ในการพิสูจน์ตรวจสอบ กรณีอาจเกิดข้อพิพาทภายในอายุความตามที่กฎหมายกำหนดเป็นระยะเวลาไม่เกิน 10 ปี ทั้งนี้ บริษัทจะลบหรือทำลายข้อมูลส่วนบุคคล หรือทำให้เป็นข้อมูลที่ไม่สามารถระบุถึงตัวตนของท่านได้ เมื่อหมดความจำเป็นหรือสิ้นสุดระยะเวลาดังกล่าว
          </p>
        </section>

        <section className="mb-4">
          <h3 className="h5">7. การรักษาความมั่นคงปลอดภัยของข้อมูลส่วนบุคคล</h3>
          <ol>
            <li>
              บริษัทจะจัดให้มีมาตรการรักษาความมั่นคงปลอดภัยของข้อมูลส่วนบุคคลอย่างเหมาะสมและสอดคล้องกับกฎหมาย นโยบาย ระเบียบ ข้อกำหนด และแนวปฏิบัติด้านการคุ้มครองข้อมูลส่วนบุคคล
            </li>
            <li>
              บริษัทสนับสนุนและส่งเสริมให้พนักงานมีความรู้และตระหนักถึงหน้าที่และความรับผิดชอบในการเก็บรวบรวม การจัดเก็บรักษา การใช้ และการเปิดเผยข้อมูลส่วนบุคคลของเจ้าของข้อมูล เพื่อให้บริษัทสามารถปฏิบัติตามนโยบายและกฎหมายคุ้มครองข้อมูลส่วนบุคคลได้อย่างถูกต้องและมีประสิทธิภาพ
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">8. สิทธิของเจ้าของข้อมูลส่วนบุคคล</h3>
          <ol>
            <li>
              สิทธิในการเพิกถอนความยินยอมในการประมวลผลข้อมูลส่วนบุคคลที่ได้ให้ความยินยอมไว้ ทั้งนี้ การเพิกถอนความยินยอมย่อมจะไม่ส่งผลกระทบต่อการเก็บรวบรวม ใช้ หรือเปิดเผยข้อมูลส่วนบุคคลที่ได้ให้ความยินยอมไว้แล้ว และบริษัทอาจประมวลผลข้อมูลส่วนบุคคลของท่านต่อไป หากบริษัทสามารถใช้ฐานอื่นในการประมวลผลข้อมูลส่วนบุคคลของท่าน
            </li>
            <li>
              สิทธิขอเข้าถึงและขอรับสำเนาข้อมูลส่วนบุคคลที่เกี่ยวกับท่าน หรือขอให้เปิดเผยถึงการได้มาซึ่งข้อมูลส่วนบุคคลดังกล่าวที่ท่านไม่ได้ให้ความยินยอม
            </li>
            <li>
              สิทธิในการขอแก้ไขข้อมูลส่วนบุคคลให้ถูกต้อง ท่านมีสิทธิขอแก้ไขข้อมูลส่วนบุคคลของท่านให้ถูกต้องเป็นปัจจุบัน และสมบูรณ์
            </li>
            <li>
              สิทธิขอให้ดำเนินการลบหรือทำลายข้อมูลส่วนบุคคล ท่านมีสิทธิร้องขอให้บริษัท ลบ ทำลาย หรือทำให้ข้อมูลส่วนบุคคลของท่านไม่สามารถระบุตัวตนของท่านได้ ในกรณีที่ไม่มีเหตุผลอันสมควรให้บริษัทประมวลผลข้อมูลส่วนบุคคลของท่านต่อไป โดยท่านสามารถใช้สิทธิในการขอให้บริษัท ลบข้อมูลส่วนบุคคลนี้ควบคู่ไปกับสิทธิในการคัดค้านในข้อถัดไป อย่างไรก็ตาม การใช้สิทธินี้จะต้องไม่เป็นการใช้สิทธิ เพื่อขอให้ลบข้อมูลส่วนบุคคลทั้งหมด โดยบริษัทจะพิจารณาแต่ละคำขอด้วยความระมัดระวังตามบทบัญญัติของกฎหมายที่เกี่ยวข้องกับการประมวลผลข้อมูลส่วนบุคคลของท่าน
            </li>
            <li>
              สิทธิในการคัดค้าน ท่านมีสิทธิคัดค้านการการเก็บรวบรวม ใช้ หรือเปิดเผยข้อมูลส่วนบุคคลที่เกี่ยวกับท่านที่บริษัทประมวลผลภายใต้ฐานผลประโยชน์อันชอบธรรมของบริษัท นอกจากนี้ ท่านยังมีสิทธิคัดค้านการประมวลผลข้อมูลส่วนบุคคลของท่าน หากบริษัทประมวลผลข้อมูลส่วนบุคคลของท่านเพื่อวัตถุประสงค์ทางการตลาด การบันทึกและวิเคราะห์ลักษณะทางจิตวิทยาและพฤติกรรมของบุคคล
            </li>
            <li>
              สิทธิขอให้ระงับการใช้ข้อมูลส่วนบุคคล ท่านมีสิทธิขอให้บริษัท ระงับการประมวลผลข้อมูลส่วนบุคคลของท่านชั่วคราว เช่น เมื่อท่านต้องการให้บริษัท แก้ไขข้อมูลส่วนบุคคลของท่านให้ถูกต้อง หรือเมื่อท่านร้องขอให้บริษัท พิสูจน์เหตุผล หรือฐานทางกฎหมายในการประมวลผลข้อมูลส่วนบุคคลของท่าน
            </li>
            <li>
              สิทธิในการให้โอนย้ายข้อมูลส่วนบุคคล ในบางกรณี ท่านสามารถขอให้บริษัทส่งหรือโอนข้อมูลส่วนบุคคลของท่านที่สามารถใช้งานโดยทั่วไปได้ในรูปแบบอิเล็กทรอนิกส์ไปยังผู้ควบคุมข้อมูลส่วนบุคคลอื่น สิทธิดังกล่าวนี้จะใช้ได้เฉพาะในกรณีของข้อมูลส่วนบุคคลที่ท่านส่งมอบให้แก่บริษัท และการประมวลผลข้อมูลส่วนบุคคลดังกล่าวได้กระทำโดยอาศัยความยินยอมของท่าน หรือในกรณีที่ข้อมูลส่วนบุคคลดังกล่าวจำเป็นต้องได้รับการประมวลผลเพื่อให้สามารถปฏิบัติตามภาระข้อผูกพันภายใต้สัญญาได้
            </li>
            <li>
              สิทธิในการร้องเรียนต่อคณะกรรมการผู้เชี่ยวชาญตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล ในกรณีที่บริษัทหรือผู้ประมวลผลข้อมูลส่วนบุคคล รวมทั้งพนักงานหรือผู้รับจ้างของบริษัทหรือผู้ประมวลผลข้อมูลส่วนบุคคล ฝ่าฝืนหรือไม่ปฏิบัติตามพระราชบัญญัติหรือประกาศที่ออกตามพระราชบัญญัติดังกล่าว
            </li>
          </ol>
        </section>

        <section className="mb-4">
          <h3 className="h5">9. การทบทวนและเปลี่ยนแปลงประกาศความเป็นส่วนตัว</h3>
          <p>
            บริษัทมีนโยบายในการพัฒนาและปรับปรุงการให้บริการให้มีความทันสมัย มีประสิทธิภาพและเป็นไปตามหลักธรรมาภิบาลและกฎหมายที่เกี่ยวข้องอยู่เสมอ ดังนั้นบริษัทอาจทำการปรับปรุงหรือแก้ไขประกาศฉบับนี้เป็นครั้งคราว จึงขอให้ท่านตรวจสอบประกาศนี้เป็นระยะ ๆ เพื่อที่ท่านจะได้ทราบประกาศความเป็นส่วนตัวฉบับล่าสุดของบริษัท
          </p>
        </section>

        <section className="mb-4">
          <h3 className="h5">10. ช่องทางการติดต่อบริษัท</h3>
          <address>
            บริษัท ชีส ดิจิตอล เน็ตเวิร์ค จำกัด<br />
            ที่อยู่: เลขที่ 2521/55-56 ถนนลาดพร้าว แขวงคลองเจ้าคุณสิงห์ เขตวังทองหลาง กรุงเทพมหานคร 10310<br />
            โทรศัพท์: 02-530 0185-6<br />
            อีเมล: <a href="mailto:admin@cheesediginet.com">admin@cheesediginet.com</a>
          </address>
        </section>

        <section className="mt-5">
          <small className="text-muted">
            ประกาศความเป็นส่วนตัวนี้ ปรับปรุงล่าสุดเมื่อ 1 ตุลาคม 2568
          </small>
        </section>
      </div>
    </div>
  );
};

/**
 * Simple EN placeholder to keep the page bilingual-ready.
 * Provide a short note if English is selected but content not yet provided.
 */
const EnglishNoticePlaceholder = () => (
  <div className="row gx-60 gy-2">
    <div className="col-12">
      <section className="mb-4">
        <h2 className="h4">Privacy Notice</h2>
        <p>
          Cheese Digital Network Co., Ltd. (“the Company”) respects the right to privacy and places great importance on the protection of personal data related to or transacted with the Company. Therefore, this Privacy Notice has been prepared to inform you, as the data subject, of the purposes and details regarding the collection, use, and/or disclosure of your personal data, as well as your legal rights concerning such data.
        </p>
      </section>

      <section className="mb-4">
        <h3 className="h5">1. Scope of Application</h3>
        <p>
          This Privacy Notice applies to the collection, use, disclosure, and storage of personal data carried out by the Company from customers, business partners, contractual parties, shareholders, personnel, employees, job applicants, or any other individuals with whom the Company interacts in a business context.
        </p>
      </section>

      <section className="mb-4">
        <h3 className="h5">2. Definitions</h3>
        <ol>
          <li><strong>“Personal Data”</strong> refers to any information relating to an individual that enables the identification of that individual, either directly or indirectly, but does not include information of deceased persons in particular.</li>
          <li><strong>“Sensitive Personal Data”</strong> refers to personal data that is inherently sensitive and may lead to unfair discrimination, such as race, ethnicity, political opinions, religious or philosophical beliefs, sexual behavior, criminal records, health data, disability, labor union data, genetic data, biometric data, or any other information similarly affecting the data subject, as specified by the Personal Data Protection Committee.</li>
          <li><strong>“Processing”</strong> refers to any operation involving the collection, use, disclosure, deletion, or destruction of personal data.</li>
          <li><strong>“Data Subject”</strong> refers to a natural person who owns the personal data that can be used to identify them, either directly or indirectly.</li>
          <li><strong>“Data Controller”</strong> refers to a person or legal entity with the authority and responsibility to make decisions regarding the collection, use, or disclosure of personal data.</li>
          <li><strong>“Data Processor”</strong> refers to a person or legal entity that processes personal data on behalf of or under the instruction of the Company. Such person or entity is not considered a data controller.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">3. Collection of Personal Data</h3>
        <h4 className="h6">3.1 Sources from which the Company collects your personal data:</h4>
        <ol>
          <li><strong>Directly from You</strong> – such as information you fill out or provide to the Company for the purposes of service contracts, employment contracts, inquiries, consultation, complaints, or from your use of the Company's services, including visits to the Company's website and other communication channels.</li>
          <li><strong>From Third Parties</strong> – such as data providers, business partners, social media platforms, and advertising service providers.</li>
          <li><strong>Through the Use of Cookies and Tracking Technologies</strong> – when you access and interact with the Company’s website.</li>
        </ol>
        <h4 className="h6">3.2 Types of Personal Data Collected by the Company:</h4>
        <ol>
          <li><strong>Personal Information</strong> – such as full name, age, date of birth, gender, marital status, national identification number, taxpayer identification number, passport number or other government-issued identification, mailing address, workplace, professional license, degree certificate, academic transcripts, bank account number, phone number, email address, LINE ID, IP address, Cookie ID, browser information, and other data related to the devices you use to access the Company’s website and other communication channels.</li>
          <li><strong>Information related to internet or other electronic network activity</strong> – such as your browsing history, search activities, and interactions with the Company’s website or advertisements, including data collected through cookies and other tracking technologies.</li>
          <li><strong>Audio, visual, or other electronic data</strong> – that may be used to identify an individual, including geolocation information.</li>
          <li><strong>Sensitive Personal Data</strong> – the Company may collect, use, and/or disclose sensitive personal data such as racial or ethnic origin, religion, health information, biometric data, disability status, or other sensitive data only when explicit consent has been obtained from you, or when permitted by law.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">4. Purposes of Collecting or Using Personal Data</h3>
        <ol>
          <li><strong>To provide services tailored to your needs:</strong> The Company will use your personal data to enable the provision of services that meet your objectives, either under a contract or as per your request.</li>
          <li><strong>To comply with legal obligations:</strong> The Company may collect, use, and/or disclose your personal data in order to comply with relevant laws such as tax laws, anti-money laundering laws, computer laws, and other applicable regulations. This may include data relating to directors, authorized representatives, or representatives of corporate clients. Additionally, the Company is required to comply with orders from government authorities or authorized officials under the law. If the Company is required to collect and/or disclose your personal data for the purpose of complying with legal obligations or entering into a contract with you, failure to provide such data may result in the Company being unable to deliver (or continue to provide) products and/or services to you.</li>
          <li><strong>To carry out activities under the Company’s or a third party's legitimate interests</strong>, within a reasonable expectation, including but not limited to:
            <ol>
              <li>Audio or video recording via CCTV prior to entering the premises.</li>
              <li>Maintaining customer relationships, such as handling complaints, conducting satisfaction surveys, providing customer care by Company staff, or sending notifications or offering products and/or services of a similar type to those you already use, which may benefit you.</li>
              <li>Risk management, compliance, internal audits, and internal organizational operations, including the prevention of illegal activities such as fraud, cyber threats, money laundering, and other violations of the law.</li>
              <li>Anonymization of personal data to make it unidentifiable.</li>
              <li>Business purposes, such as statistical analysis or Company promotion, including audio or video recordings of meetings, training sessions, recreational events, or exhibitions.</li>
            </ol>
          </li>
          <li><strong>To enable you to receive the benefits of services you have consented to:</strong> For example, to provide better and more tailored services, offer exclusive deals, privileges, recommendations, news, and the right to participate in special activities.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">5. Disclosure of Personal Data</h3>
        <ol>
          <li>The Company will not disclose your personal data to any third party without your consent and will only disclose such data for the purposes that have been previously notified.</li>
          <li>The Company may need to disclose your personal data to business partners or other individuals or entities, both domestic and international, for the benefit of the Company’s operations and to provide services to the data subject. In doing so, the Company will require such parties to keep the data confidential and not to use it for purposes beyond those defined by the Company.</li>
          <li>The Company may disclose your personal data as required by law, including disclosure to government authorities, regulatory agencies, or upon lawful requests made under legal authority.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">6. Retention Period of Personal Data</h3>
        <p>
          The Company will retain your personal data for as long as necessary to fulfill the purposes outlined in this policy. In some cases, the data may be retained beyond that period if required or permitted by law—for example, under anti-money laundering laws or for purposes of verification, audits, or legal dispute resolution within the applicable statute of limitations, which shall not exceed 10 years. Once the data is no longer necessary or the retention period has ended, the Company will delete, destroy, or anonymize your personal data so that it can no longer be linked to you.
        </p>
      </section>

      <section className="mb-4">
        <h3 className="h5">7. Security Measures for Personal Data</h3>
        <ol>
          <li>The Company will implement appropriate security measures to protect personal data, in accordance with applicable laws, policies, regulations, standards, and best practices related to data protection.</li>
          <li>The Company promotes and encourages awareness among its employees regarding their responsibilities in collecting, storing, using, and disclosing personal data. This is to ensure compliance with this policy and personal data protection laws effectively and accurately.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">8. Rights of Data Subjects</h3>
        <ol>
          <li><strong>Right to Withdraw Consent:</strong> You have the right to withdraw your consent for the processing of your personal data at any time. However, such withdrawal will not affect the collection, use, or disclosure of personal data that was previously consented to. The Company may continue processing your data if there is a legal basis other than consent for doing so.</li>
          <li><strong>Right of Access:</strong> You have the right to access and obtain a copy of your personal data held by the Company, as well as request disclosure of how the Company acquired your data if it was not directly obtained from you.</li>
          <li><strong>Right to Rectification:</strong> You have the right to request that your personal data be corrected, updated, or completed if it is inaccurate, outdated, or incomplete.</li>
          <li><strong>Right to Erasure:</strong> You may request the deletion, destruction, or anonymization of your personal data where there is no valid reason for the Company to continue processing it. This right may be exercised together with the right to object (outlined below). However, this does not imply an automatic deletion of all data, and each request will be considered carefully in accordance with the applicable personal data protection laws.</li>
          <li><strong>Right to Object:</strong> You have the right to object to the collection, use, or disclosure of your personal data when it is based on the Company’s legitimate interest. Additionally, you may object to the processing of your personal data for direct marketing or for profiling purposes based on behavioral or psychological analysis.</li>
          <li><strong>Right to Restriction of Processing:</strong> You have the right to request a temporary suspension of the processing of your personal data, such as when you request data correction or ask the Company to verify the legal basis for data processing.</li>
          <li><strong>Right to Data Portability:</strong> In certain cases, you may request that your personal data be provided to you or transferred to another data controller in a commonly used, machine-readable electronic format. This right applies only to data you have provided to the Company, where processing is based on your consent or necessary for fulfilling a contractual obligation.</li>
          <li><strong>Right to Lodge a Complaint:</strong> You have the right to lodge a complaint with the Expert Committee under the Personal Data Protection Act if the Company, its data processors, employees, or contractors violate or fail to comply with the Act or any relevant notifications issued under it.</li>
        </ol>
      </section>

      <section className="mb-4">
        <h3 className="h5">9. Review and Changes to the Privacy Notice</h3>
        <p>
          The Company is committed to continuously improving its services to ensure they are modern, effective, and comply with good governance and applicable laws. Therefore, the Company may update or amend this Privacy Notice from time to time. You are encouraged to review this notice periodically to stay informed of any changes.
        </p>
      </section>

      <section className="mb-4">
        <h3 className="h5">10. Contact Information</h3>
        <address>
          Cheese Digital Network Co., Ltd.<br/>
          Address: 2521/55-56 Ladprao Road, Khongchaokhunsing Sub-district, Wangthonglang District, Bangkok 10310<br/>
          Phone: 02-530 0185-6<br/>
          Email: <a href="mailto:admin@cheesediginet.com">admin@cheesediginet.com</a>
        </address>
      </section>

      <section className="mt-5"><small className="text-muted">This Privacy Notice was last updated on October 1, 2025.</small></section>
    </div>
  </div>
);
